import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaClock, FaUser, FaBuilding, FaBriefcase, FaEnvelope, FaPhone, FaGlobe, FaCheckCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import PublicNav from '../../components/PublicNav/PublicNav';
import PublicFooter from '../../components/PublicFooter/PublicFooter';
import { createDemoRequest, getAvailableTimeSlots } from '../../services/demoRequestService';
import './ScheduleDemo.css';

const ScheduleDemo = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    jobTitle: '',
    companySize: '',
    industry: '',
    preferredDate: '',
    preferredTime: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    message: ''
  });

  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const companySizes = [
    '1-10',
    '11-50',
    '51-200',
    '201-500',
    '501-1000',
    '1000+'
  ];

  const industries = [
    'Technology',
    'Healthcare',
    'Finance',
    'Education',
    'Manufacturing',
    'Retail',
    'Construction',
    'Consulting',
    'Government',
    'Other'
  ];

  const timeSlots = [
    '09:00 AM',
    '10:00 AM',
    '11:00 AM',
    '12:00 PM',
    '01:00 PM',
    '02:00 PM',
    '03:00 PM',
    '04:00 PM',
    '05:00 PM'
  ];

  // Get minimum date (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Get maximum date (3 months from now)
  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 3);
    return maxDate.toISOString().split('T')[0];
  };

  // Fetch available time slots when date changes
  useEffect(() => {
    if (formData.preferredDate) {
      fetchAvailableSlots(formData.preferredDate);
    }
  }, [formData.preferredDate]);

  const fetchAvailableSlots = async (date) => {
    try {
      const response = await getAvailableTimeSlots(date);
      setAvailableSlots(response.availableSlots || timeSlots);
    } catch (error) {
      console.error('Error fetching available slots:', error);
      setAvailableSlots(timeSlots);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await createDemoRequest(formData);
      toast.success(response.message || 'Demo request submitted successfully!');
      setSubmitted(true);
      
      // Reset form after 5 seconds
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          company: '',
          jobTitle: '',
          companySize: '',
          industry: '',
          preferredDate: '',
          preferredTime: '',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          message: ''
        });
      }, 5000);
    } catch (error) {
      toast.error(error.error || 'Failed to submit demo request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="schedule-demo-page">
        <PublicNav />
        <section className="success-section">
          <div className="container">
            <motion.div
              className="success-content"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="success-icon">
                <FaCheckCircle />
              </div>
              <h1>Thank You!</h1>
              <p className="success-message">
                Your demo request has been submitted successfully. We'll contact you shortly to confirm your appointment.
              </p>
              <div className="success-details">
                <p><strong>Name:</strong> {formData.firstName} {formData.lastName}</p>
                <p><strong>Email:</strong> {formData.email}</p>
                <p><strong>Company:</strong> {formData.company}</p>
                <p><strong>Preferred Date:</strong> {new Date(formData.preferredDate).toLocaleDateString()}</p>
                <p><strong>Preferred Time:</strong> {formData.preferredTime}</p>
              </div>
              <p className="success-note">
                Please check your email for confirmation details.
              </p>
            </motion.div>
          </div>
        </section>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="schedule-demo-page">
      <PublicNav />
      
      {/* Hero Section */}
      <section className="demo-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="demo-hero-content"
          >
            <h1 className="demo-title">Schedule a Demo</h1>
            <p className="demo-subtitle">
              See how our Project Portfolio Management solution can transform your business. 
              Book a personalized demo with our team.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Demo Form Section */}
      <section className="demo-form-section">
        <div className="container">
          <div className="demo-grid">
            {/* Form */}
            <motion.div
              className="demo-form-wrapper"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <form onSubmit={handleSubmit} className="demo-form">
                {/* Personal Information */}
                <div className="form-section">
                  <h3 className="section-title">
                    <FaUser /> Personal Information
                  </h3>
                  <div className="form-row custom-row">
                    <div className="form-group">
                      <label htmlFor="firstName">First Name *</label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        placeholder="John"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="lastName">Last Name *</label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        placeholder="Doe"
                      />
                    </div>
                  </div>

                  <div className="form-row custom-row">
                    <div className="form-group">
                      <label htmlFor="email">Email Address *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@company.com"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="phone">Phone Number</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>
                </div>

                {/* Company Information */}
                <div className="form-section">
                  <h3 className="section-title">
                    <FaBuilding /> Company Information
                  </h3>
                  <div className="form-group">
                    <label htmlFor="company">Company Name *</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      placeholder="Acme Corporation"
                    />
                  </div>

                  <div className="form-row custom-row">
                    <div className="form-group">
                      <label htmlFor="jobTitle">Job Title</label>
                      <input
                        type="text"
                        id="jobTitle"
                        name="jobTitle"
                        value={formData.jobTitle}
                        onChange={handleChange}
                        placeholder="Project Manager"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="companySize">Company Size</label>
                      <select
                        id="companySize"
                        name="companySize"
                        value={formData.companySize}
                        onChange={handleChange}
                      >
                        <option value="">Select size</option>
                        {companySizes.map(size => (
                          <option key={size} value={size}>{size} employees</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="industry">Industry</label>
                    <select
                      id="industry"
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                    >
                      <option value="">Select industry</option>
                      {industries.map(industry => (
                        <option key={industry} value={industry}>{industry}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Schedule Information */}
                <div className="form-section">
                  <h3 className="section-title">
                    <FaCalendarAlt /> Schedule Your Demo
                  </h3>
                  <div className="form-row custom-row">
                    <div className="form-group">
                      <label htmlFor="preferredDate">Preferred Date *</label>
                      <input
                        type="date"
                        id="preferredDate"
                        name="preferredDate"
                        value={formData.preferredDate}
                        onChange={handleChange}
                        required
                        min={getMinDate()}
                        max={getMaxDate()}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="preferredTime">Preferred Time *</label>
                      <select
                        id="preferredTime"
                        name="preferredTime"
                        value={formData.preferredTime}
                        onChange={handleChange}
                        required
                        disabled={!formData.preferredDate}
                      >
                        <option value="">Select time</option>
                        {availableSlots.map(slot => (
                          <option key={slot} value={slot}>{slot}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="timezone">Timezone</label>
                    <input
                      type="text"
                      id="timezone"
                      name="timezone"
                      value={formData.timezone}
                      onChange={handleChange}
                      readOnly
                    />
                  </div>

                  <div className="form-group ">
                    <label htmlFor="message">Additional Information</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="4"
                      className='mb-4'
                      placeholder="Tell us about your project management needs..."
                    />
                  </div>
                </div>

                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Schedule Demo'}
                </button>
              </form>
            </motion.div>

            {/* Benefits Sidebar */}
            <motion.div
              className="demo-benefits-wrapper"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="benefits-title">What to Expect</h2>
              <div className="benefits-list">
                <div className="benefit-item">
                  <div className="benefit-icon">
                    <FaClock />
                  </div>
                  <div className="benefit-content">
                    <h4>30-Minute Session</h4>
                    <p>A focused demo tailored to your specific needs and use cases.</p>
                  </div>
                </div>

                <div className="benefit-item">
                  <div className="benefit-icon">
                    <FaUser />
                  </div>
                  <div className="benefit-content">
                    <h4>Expert Guidance</h4>
                    <p>Learn from our product specialists who understand your industry.</p>
                  </div>
                </div>

                <div className="benefit-item">
                  <div className="benefit-icon">
                    <FaBriefcase />
                  </div>
                  <div className="benefit-content">
                    <h4>Real-World Examples</h4>
                    <p>See how companies like yours are succeeding with our platform.</p>
                  </div>
                </div>

                <div className="benefit-item">
                  <div className="benefit-icon">
                    <FaGlobe />
                  </div>
                  <div className="benefit-content">
                    <h4>Q&A Session</h4>
                    <p>Get all your questions answered by our team of experts.</p>
                  </div>
                </div>
              </div>

              <div className="demo-note">
                <p>
                  <strong>Note:</strong> After submitting this form, you'll receive a confirmation 
                  email with meeting details and a calendar invite.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default ScheduleDemo;

